import "./insScatterPlotDatapointChart.scss";
import Highcharts from "highcharts/highstock";
import markerClusters from "highcharts/modules/marker-clusters";
import React, { useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { InsHighcharts } from "../../base";
import { formatPointLabel, formatPointLabelForAxis, getChartSelectValueProps } from "../base/utils";
import { numberFormatters } from "common/number-formatters";
import { formatNegativeValue } from "common/number-helper";
import { NumberFormatKind } from "common/types";
import { InsLabelValueTooltip } from "components/widgets/base/common/insLabelValueTooltip";
import { InsQueryStatedElement } from "components/widgets/base/insQueryStatedElement";
import { useSelectedValues, useUnSelectedLegends, useWidgetComponentProps } from "components/widgets/hooks/use-current-widget";
import { useGetSelectedCurrency } from "components/workspaces/hooks/use-current-dashboard";
import { useGetDatapointsWidgetData } from "hooks/widget-data/use-datapoints";
import { language } from "language/default";
const formatEquation = (intercept, slope) => {
    const equationParts = [], formattedIntercept = numberFormatters(intercept, NumberFormatKind.default), formattedSlope = numberFormatters(slope, NumberFormatKind.default);
    if (slope === 1) {
        equationParts.push("x");
    }
    else if (slope !== 0) {
        equationParts.push(`${slope >= 0 ? formattedSlope : formatNegativeValue(formattedSlope)}x`);
    }
    if (intercept !== 0) {
        equationParts.push(intercept >= 0 ? formattedIntercept : formatNegativeValue(formattedIntercept));
    }
    return equationParts.length > 0 ? `y = ${equationParts.join(intercept >= 0 ? " + " : " ")}` : "";
};
markerClusters(Highcharts);
export const InsScatterPlotDatapointChart = (props) => {
    const { sectionId, widgetId } = props, selectedValues = useSelectedValues(sectionId, widgetId), seletedCurrency = useGetSelectedCurrency(), unSelectedLegends = useUnSelectedLegends(sectionId, widgetId), widgetProps = useWidgetComponentProps(sectionId, widgetId), { colorSet, limitCluster, displayLinearRegression } = widgetProps;
    const [[rawResult], queryState] = useGetDatapointsWidgetData(props), [result] = rawResult?.data || [], { items: points, hasColorDimension, series, lineData, hasData } = result || {
        hasColorDimension: false,
        hasData: false,
        items: [],
        series: []
    };
    const clusterIsEnabled = useMemo(() => limitCluster &&
        (points?.filter((point) => !unSelectedLegends?.includes(point.dataColor)).length > limitCluster), [unSelectedLegends, points, limitCluster]), [{ displayName: yAxisDisplayName, valueFormat: yAxisValueFormat, valueUnit: yAxisValueUnit }, { displayName: xAxisDisplayName, valueFormat: xAxisValueFormat, valueUnit: xAxisValueUnit }] = getChartSelectValueProps(selectedValues, widgetProps);
    const options = {
        chart: {
            animation: false,
            className: "highchart-scatterplot-chart",
            // eslint-disable-next-line no-magic-numbers
            spacing: [22, 10, 16, 10],
            styledMode: true
        },
        credits: { enabled: false },
        legend: { enabled: hasColorDimension || Boolean(displayLinearRegression) },
        plotOptions: {
            scatter: {
                cluster: { allowOverlap: false, enabled: clusterIsEnabled },
                dataLabels: { enabled: true, formatter: () => "" },
                turboThreshold: 0
            },
            series: { stickyTracking: false }
        },
        series,
        title: { text: "" },
        tooltip: {
            formatter() {
                const labelValuePairs = [];
                const pt = this.point;
                if (pt.clusterPointsAmount) {
                    return `${language.en.chart.map.tooltip.companies}: ${pt.clusterPointsAmount}`;
                }
                if (pt.tooltips?.length) {
                    pt.tooltips.forEach(([label, value]) => {
                        labelValuePairs.push([label, value]);
                    });
                    labelValuePairs.push([
                        yAxisDisplayName,
                        formatPointLabel(pt.dataY, yAxisValueFormat, yAxisValueUnit, { currency: seletedCurrency })
                    ]);
                    labelValuePairs.push([
                        xAxisDisplayName,
                        formatPointLabel(pt.dataX, xAxisValueFormat, xAxisValueUnit, { currency: seletedCurrency })
                    ]);
                }
                else if (lineData) {
                    labelValuePairs.push([language.en.chart.scatterPlot.linearRegression, formatEquation(lineData.intercept, lineData.slope)]);
                }
                return renderToStaticMarkup(React.createElement(InsLabelValueTooltip, { labelValuePairs: labelValuePairs }));
            }
        },
        xAxis: {
            labels: {
                formatter() {
                    return formatPointLabelForAxis(this.value, xAxisValueFormat, xAxisValueUnit, { currency: seletedCurrency, maximumFractionDigits: 0 });
                }
            },
            title: { text: xAxisDisplayName }
        },
        yAxis: {
            labels: {
                formatter() {
                    return formatPointLabelForAxis(this.value, yAxisValueFormat, yAxisValueUnit, { currency: seletedCurrency, maximumFractionDigits: 0 });
                }
            },
            title: { text: yAxisDisplayName }
        }
    };
    return React.createElement(InsQueryStatedElement, { widgetFactoryProps: props, queryState: queryState, hasData: hasData },
        React.createElement(InsHighcharts, { key: `${sectionId}-${widgetId}-${String(clusterIsEnabled)}`, options: options, colorSet: colorSet, sectionId: sectionId, widgetId: widgetId, isMaximizedOne: props.isMaximizedOne }));
};
